import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, CameraOff, Download, FileText, Loader2, PackageCheck, Trash2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { apiFile, createNir, errText, fetchNirs, fetchStock, importNeoNir, lookupEan, ron } from "@/lib/bf";
import { DemoTag } from "./BFStockTotal";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Bucharest" });

const Scanner = ({ onCode }) => {
  const [on, setOn] = useState(false);
  const last = useRef({ t: "", at: 0 });
  useEffect(() => {
    if (!on) return undefined;
    const s = new Html5Qrcode("bf-scan-box");
    s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 260, height: 120 } }, (t) => {
      if (t === last.current.t && Date.now() - last.current.at < 2000) return;
      last.current = { t, at: Date.now() };
      onCode(t);
    }, () => {}).catch(() => { toast.error("Nu pot porni camera. Permite accesul la cameră sau scrie codul."); setOn(false); });
    return () => { if (s.isScanning) s.stop().catch(() => {}); };
  }, [on, onCode]);
  return (
    <div className="bf-scan">
      <button type="button" className="st-btn st-btn--ghost" onClick={() => setOn(!on)} data-testid="bf-scan-toggle">{on ? <><CameraOff size={14} /> Oprește camera</> : <><Camera size={14} /> Scanează produsele</>}</button>
      {on && <div id="bf-scan-box" className="bf-scan-box" data-testid="bf-scan-box" />}
    </div>
  );
};

const Picker = ({ ean, onPick }) => {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => { if (q.length < 2) return undefined; const t = setTimeout(() => fetchStock(q).then(setOpts).catch(() => {}), 250); return () => clearTimeout(t); }, [q]);
  return (
    <div className="bf-rec-pick" data-testid="bf-rec-picker">
      <p className="st-hint">{ean ? <>Codul <b>{ean}</b> e nou. Alege produsul o dată, îl țin minte.</> : "Adaugă manual un produs."}</p>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută după nume sau cod" data-testid="bf-rec-pick-search" />
      <ul>{opts.slice(0, 8).map((o) => <li key={o.code}><button type="button" onClick={() => onPick(o)} data-testid={`bf-rec-pick-${o.code}`}>{o.name} <small>{o.code}</small></button></li>)}</ul>
    </div>
  );
};

export default function BFReception({ firms }) {
  const [head, setHead] = useState({ firm: firms[0]?.id || "", supplier: "", doc: "", entry: today() });
  const [lines, setLines] = useState([]);
  const [pending, setPending] = useState(null);
  const [nirs, setNirs] = useState([]);
  const [busy, setBusy] = useState("");
  const known = useRef({});
  const loadNirs = useCallback(() => fetchNirs().then(setNirs).catch(() => {}), []);
  useEffect(() => { loadNirs(); }, [loadNirs]);
  const add = (p, ean = "") => setLines((ls) => {
    const i = ls.findIndex((l) => l.code === p.code);
    if (i >= 0) return ls.map((l, j) => (j === i ? { ...l, q: l.q + 1 } : l));
    return [...ls, { code: p.code, name: p.name, q: 1, cost: String(p.cost ?? ""), ean }];
  });
  const scanned = useCallback(async (ean) => {
    const p = known.current[ean] || await lookupEan(ean).catch(() => null);
    if (p) { add(p, ean); toast.success(`+1 ${p.name}`); } else setPending({ ean });
  }, []);
  const setL = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: k === "q" ? Number(v.replace(/\D/g, "")) || 0 : v } : l)));
  const close = () => {
    if (!lines.length) return toast.error("Scanează cel puțin un produs");
    if (lines.some((l) => !(l.q > 0))) return toast.error("Fiecare produs trebuie să aibă cantitate");
    if (lines.some((l) => !(Number(String(l.cost).replace(",", ".")) > 0))) return toast.error("Completează prețul de achiziție la fiecare produs");
    setBusy("nir");
    createNir({ ...head, lines: lines.map((l) => ({ code: l.code, q: l.q, cost: Number(String(l.cost).replace(",", ".")) || 0, ean: l.ean })) })
      .then((n) => { toast.success(`${n.no} generat · ${n.lines.length} produse intrate în stoc`, { duration: 12000, action: { label: "Deschide NIR", onClick: () => window.open(apiFile(n.pdf), "_blank") } }); setLines([]); setPending(null); loadNirs(); })
      .catch((e) => toast.error(errText(e, "Nu am putut genera NIR-ul"))).finally(() => setBusy(""));
  };
  const neo = () => { setBusy("neo"); importNeoNir(head.firm).then((n) => { toast.success(`${n.no} importat din NeoManager`); loadNirs(); }).catch((e) => toast.error(errText(e, "Import eșuat"))).finally(() => setBusy("")); };
  return (
    <>
      <section className="invite-box" data-testid="bf-reception">
        <h3><PackageCheck size={18} /> Recepție marfă cu NIR automat <DemoTag /></h3>
        <p className="st-hint">Scanezi fiecare produs: o scanare adaugă o bucată. La final apeși „Închide recepția”, iar NIR-ul se generează singur și marfa intră în stoc cu data de azi, care dă vechimea lotului.</p>
        <div className="bf-rec-grid">
          <label><span>Firmă</span><select value={head.firm} onChange={(e) => setHead({ ...head, firm: e.target.value })} data-testid="bf-rec-firm">{firms.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
          <label><span>Furnizor</span><input value={head.supplier} onChange={(e) => setHead({ ...head, supplier: e.target.value })} data-testid="bf-rec-supplier" /></label>
          <label><span>Nr. factură furnizor</span><input value={head.doc} onChange={(e) => setHead({ ...head, doc: e.target.value })} data-testid="bf-rec-doc" /></label>
          <label><span>Data intrării</span><input type="date" value={head.entry} max={today()} onChange={(e) => setHead({ ...head, entry: e.target.value })} data-testid="bf-rec-entry" /></label>
        </div>
        <Scanner onCode={scanned} />
        <label className="bf-inv-field bf-rec-manual"><span>Sau scrie / lipește codul de bare și apasă Enter</span>
          <input onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { scanned(e.target.value.trim()); e.target.value = ""; } }} data-testid="bf-rec-ean" /></label>
        {pending && <Picker ean={pending.ean} onPick={(p) => { if (pending.ean) known.current[pending.ean] = p; add(p, pending.ean); setPending(null); }} />}
        {!pending && <button type="button" className="st-btn st-btn--ghost bf-rec-addbtn" onClick={() => setPending({ ean: "" })} data-testid="bf-rec-add-manual">+ Produs fără cod de bare</button>}
        {lines.length > 0 && (
          <ul className="bf-rec-lines" data-testid="bf-rec-lines">
            {lines.map((l, i) => (
              <li key={l.code} data-testid={`bf-rec-line-${l.code}`}>
                <span>{l.name}<small>{l.code}</small></span>
                <input inputMode="numeric" value={l.q} onChange={(e) => setL(i, "q", e.target.value)} aria-label="Cantitate" data-testid={`bf-rec-line-q-${l.code}`} />
                <input inputMode="decimal" value={l.cost} onChange={(e) => setL(i, "cost", e.target.value)} aria-label="Preț achiziție" data-testid={`bf-rec-line-cost-${l.code}`} />
                <button type="button" onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="Șterge" data-testid={`bf-rec-line-del-${l.code}`}><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )}
        <div className="share-actions">
          <button type="button" className="st-btn st-btn--silver" onClick={close} disabled={busy === "nir" || !lines.length} data-testid="bf-rec-close">{busy === "nir" ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />} Închide recepția și generează NIR ({lines.reduce((s, l) => s + l.q, 0)} buc.)</button>
          <button type="button" className="st-btn st-btn--ghost" onClick={neo} disabled={busy === "neo"} data-testid="bf-rec-import-neo">{busy === "neo" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Preia NIR-urile din NeoManager</button>
        </div>
      </section>
      <section className="invite-box" data-testid="bf-nirs">
        <h3><FileText size={18} /> NIR-uri ({nirs.length})</h3>
        {!nirs.length && <p className="st-hint" data-testid="bf-nirs-empty">Niciun NIR încă.</p>}
        <ul className="bfa-leads">
          {nirs.map((n) => (
            <li key={n.id} data-testid={`bf-nir-${n.no}`}>
              <div><b>{n.no} · {n.firm_name}</b><span>{n.date.split("-").reverse().join(".")} · {n.source === "scanare" ? "scanare" : "din NeoManager"} · {n.supplier || "-"} · {n.lines.length} produse, {n.lines.reduce((s, l) => s + l.q, 0)} buc.</span></div>
              <div className="interested-actions"><span className="bfa-pf-total">{ron(n.total)}</span>
                <a className="st-btn st-btn--ghost" href={apiFile(n.pdf)} target="_blank" rel="noopener noreferrer" data-testid={`bf-nir-pdf-${n.no}`}><FileText size={14} /> PDF</a></div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
